import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AppLayout } from "../components/AppLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Users, CreditCard, PhoneCall, AlertTriangle, BellRing, DollarSign, Loader2,
  Clock, Zap, ShieldCheck, Download, Sparkles, UserPlus,
} from "lucide-react";
import { Badge } from "../components/ui/badge";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ticking, setTicking] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const forceTick = async () => {
    setTicking(true);
    try {
      await api.post("/admin/scheduler/tick");
      await load();
      toast.success("Scheduler tick fired");
    } catch {
      toast.error("Tick failed");
    } finally { setTicking(false); }
  };

  if (loading || !stats) {
    return <AppLayout><div className="grid place-items-center h-96"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div></AppLayout>;
  }

  const cards = [
    { icon: Users, label: "Total users", value: stats.total_users },
    { icon: CreditCard, label: "Paying users", value: stats.paying_users },
    { icon: PhoneCall, label: "Calls today", value: stats.calls_today },
    { icon: AlertTriangle, label: "Failed calls", value: stats.failed_calls },
    { icon: BellRing, label: "Alerts today", value: stats.alerts_today },
    { icon: DollarSign, label: "MRR estimate", value: `$${stats.mrr_estimate}` },
  ];

  const sched = stats.scheduler || {};

  return (
    <AppLayout
      title="Admin"
      subtitle="Operational overview of CherishLink."
      actions={
        <Button onClick={forceTick} disabled={ticking} className="rounded-full" variant="outline" data-testid="admin-force-tick">
          {ticking ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />} Force scheduler tick
        </Button>
      }
    >
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {cards.map((c) => (
          <Card key={c.label} className="rounded-3xl border-border/60 soft-shadow hover-lift" data-testid={`admin-${c.label.toLowerCase().replace(/ /g, "-")}`}>
            <CardContent className="p-6 flex items-start justify-between">
              <div>
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{c.label}</div>
                <div className="mt-2 font-display text-3xl font-bold tracking-tight">{c.value}</div>
              </div>
              <div className="h-11 w-11 rounded-2xl bg-accent grid place-items-center">
                <c.icon className="h-5 w-5 text-primary" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="rounded-3xl border-border/60 soft-shadow mt-6" data-testid="admin-scheduler">
        <CardContent className="p-6 md:p-8">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-display text-lg font-semibold flex items-center gap-2">
                <Clock className="h-4 w-4 text-primary" /> Call scheduler
              </h3>
              <p className="text-sm text-muted-foreground mt-1">Fires outbound calls at each loved one's preferred time.</p>
            </div>
            <Badge className={sched.running ? "bg-secondary/15 text-secondary" : "bg-muted text-muted-foreground"}>
              {sched.running ? "Running" : "Stopped"}
            </Badge>
          </div>
          <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <KV label="Provider" value={
              <Badge variant="secondary" className="bg-accent text-accent-foreground rounded-full capitalize">
                {sched.provider || "mock"}
              </Badge>
            } />
            <KV label="Fired total" value={sched.fired_total ?? 0} />
            <KV label="Last tick" value={sched.last_tick ? new Date(sched.last_tick).toLocaleTimeString() : "—"} />
            <KV label="Safe-list size" value={
              <span className="inline-flex items-center gap-1">
                <ShieldCheck className="h-3.5 w-3.5 text-secondary" /> {sched.safelist_count ?? 0}
              </span>
            } />
          </div>
          {sched.jobs?.length > 0 && (
            <div className="mt-5">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Next runs</div>
              <div className="space-y-1.5">
                {sched.jobs.map((j) => (
                  <div key={j.id} className="text-sm flex items-center gap-4">
                    <code className="text-xs bg-muted px-2 py-0.5 rounded">{j.id}</code>
                    <span className="text-muted-foreground">{j.trigger}</span>
                    <span>{j.next_run_time ? new Date(j.next_run_time).toLocaleString() : "—"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="rounded-3xl border-border/60 soft-shadow mt-6" data-testid="admin-recent-users">
        <CardContent className="p-6 md:p-8">
          <h3 className="font-display text-lg font-semibold">Recent signups</h3>
          <div className="mt-5 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Joined</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.recent_users.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="text-muted-foreground">{u.email}</TableCell>
                    <TableCell><Badge variant="secondary" className="bg-accent text-accent-foreground rounded-full capitalize">{u.plan}</Badge></TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <AdminEmailsCard />
      <AdminPaymentsCard />
      <AdminTestCallsCard />
      <AdminKinvantaCard />
    </AppLayout>
  );
}

function AdminEmailsCard() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/admin/emails?limit=10").then(({ data }) => setRows(data.emails || []));
  }, []);
  return (
    <Card className="rounded-3xl border-border/60 soft-shadow mt-6" data-testid="admin-emails">
      <CardContent className="p-6 md:p-8">
        <h3 className="font-display text-lg font-semibold">Recent emails sent</h3>
        <p className="text-sm text-muted-foreground mt-1">Post-call summaries + alert emails (currently via the mock provider).</p>
        {rows === null ? (
          <div className="py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : rows.length === 0 ? (
          <div className="py-6 text-sm text-muted-foreground">No emails yet.</div>
        ) : (
          <div className="mt-5 overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Subject</TableHead>
                <TableHead>To</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Sent</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="font-medium">{e.subject}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{e.to}</TableCell>
                    <TableCell><Badge variant="secondary" className="bg-accent text-accent-foreground rounded-full capitalize">{e.category}</Badge></TableCell>
                    <TableCell className="capitalize">{e.provider}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {e.created_at ? new Date(e.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AdminPaymentsCard() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/admin/payments?limit=10").then(({ data }) => setRows(data.transactions || []));
  }, []);
  return (
    <Card className="rounded-3xl border-border/60 soft-shadow mt-6" data-testid="admin-payments">
      <CardContent className="p-6 md:p-8">
        <h3 className="font-display text-lg font-semibold">Recent Stripe transactions</h3>
        <p className="text-sm text-muted-foreground mt-1">Live Stripe Checkout sessions (test mode).</p>
        {rows === null ? (
          <div className="py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : rows.length === 0 ? (
          <div className="py-6 text-sm text-muted-foreground">No transactions yet.</div>
        ) : (
          <div className="mt-5 overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Plan</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Session</TableHead>
                <TableHead>Created</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="capitalize font-medium">{t.plan}</TableCell>
                    <TableCell>${t.amount} {t.currency?.toUpperCase()}</TableCell>
                    <TableCell>
                      <Badge className={
                        t.payment_status === "paid" ? "bg-secondary/15 text-secondary rounded-full" :
                        t.payment_status === "initiated" ? "bg-accent text-accent-foreground rounded-full" :
                        "bg-muted text-muted-foreground rounded-full"
                      }>{t.payment_status}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground font-mono">{(t.session_id || "").slice(0, 16)}…</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {t.created_at ? new Date(t.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function KV({ label, value }) {
  return (
    <div className="p-4 rounded-2xl border border-border/60 bg-white">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 font-semibold">{value}</div>
    </div>
  );
}

function csvDownload(path, filename) {
  const token = localStorage.getItem("cherishlink_token");
  fetch(`${API_BASE}${path}`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => {
      if (!r.ok) throw new Error("Download failed");
      return r.blob();
    })
    .then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    })
    .catch(() => toast.error("Couldn't download CSV"));
}

function AdminTestCallsCard() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/admin/test-calls?limit=50").then(({ data }) => setRows(data.rows || []));
  }, []);
  return (
    <Card className="rounded-3xl border-border/60 soft-shadow mt-6" data-testid="admin-test-calls">
      <CardContent className="p-6 md:p-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-display text-lg font-semibold flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-secondary" /> Test call leads
            </h3>
            <p className="text-sm text-muted-foreground mt-1">Prospects who requested a live demo call.</p>
          </div>
          <Button
            variant="outline" size="sm" className="rounded-full"
            onClick={() => csvDownload("/api/admin/test-calls?format=csv", "test-call-leads.csv")}
            data-testid="admin-test-calls-csv"
          >
            <Download className="h-4 w-4 mr-1.5" /> CSV
          </Button>
        </div>
        {rows === null ? (
          <div className="py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : rows.length === 0 ? (
          <div className="py-6 text-sm text-muted-foreground">No test-call requests yet.</div>
        ) : (
          <div className="mt-5 overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>When</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Duration</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-muted-foreground text-sm">
                      {r.created_at ? new Date(r.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{r.phone}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{r.email || "—"}</TableCell>
                    <TableCell>
                      <Badge className={
                        r.status === "completed" ? "bg-secondary/15 text-secondary rounded-full" :
                        r.status === "blocked" ? "bg-amber-100 text-amber-800 rounded-full" :
                        r.status === "failed" ? "bg-rose-100 text-rose-700 rounded-full" :
                        r.status === "in_progress" ? "bg-primary/10 text-primary rounded-full" :
                        "bg-muted text-muted-foreground rounded-full"
                      }>{r.status}{r.blocked_reason ? ` (${r.blocked_reason})` : ""}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.source_page}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {r.duration_minutes != null ? `${r.duration_minutes}m` : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AdminKinvantaCard() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/admin/kinvanta-waitlist?limit=100").then(({ data }) => setRows(data.rows || []));
  }, []);
  return (
    <Card className="rounded-3xl border-border/60 soft-shadow mt-6" data-testid="admin-kinvanta">
      <CardContent className="p-6 md:p-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-display text-lg font-semibold flex items-center gap-2">
              <UserPlus className="h-4 w-4 text-primary" /> Kinvanta waitlist
            </h3>
            <p className="text-sm text-muted-foreground mt-1">Phase-2 caregiver platform early-access leads.</p>
          </div>
          <Button
            variant="outline" size="sm" className="rounded-full"
            onClick={() => csvDownload("/api/admin/kinvanta-waitlist?format=csv", "kinvanta-waitlist.csv")}
            data-testid="admin-kinvanta-csv"
          >
            <Download className="h-4 w-4 mr-1.5" /> CSV
          </Button>
        </div>
        {rows === null ? (
          <div className="py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : rows.length === 0 ? (
          <div className="py-6 text-sm text-muted-foreground">No signups yet.</div>
        ) : (
          <div className="mt-5 overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>When</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Relation</TableHead>
                <TableHead>Biggest need</TableHead>
                <TableHead>Source</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-muted-foreground text-sm">
                      {r.created_at ? new Date(r.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                    </TableCell>
                    <TableCell className="font-medium">{r.email}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{r.first_name || "—"}</TableCell>
                    <TableCell className="text-sm">{r.relation || "—"}</TableCell>
                    <TableCell className="text-sm">{r.biggest_need || "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.source_page}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
